import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, NavLink, Outlet, useOutletContext } from 'react-router-dom'
import { jsonFetch } from '@/utils/jsonFetch'
import { CurrentToolsCard } from '@/components/ai/CurrentToolsCard'

export type ParentSummary = {
  id: string
  name: string | null
}

export type StudentSummary = {
  id: string
  displayName: string | null
  fullName: string | null
  grade: string | null
  classroom: {
    id: string
    name: string | null
    teacherName: string | null
    teacherUserId: string | null
  } | null
}

export type ParentActivityEntry = {
  id: string
  studentId: string | null
  studentName: string | null
  classroomName: string | null
  teacherName: string | null
  subject: string | null
  bodyPreview: string | null
  createdAt: string | null
  templateTitle: string | null
}

export type ParentHomePayload = {
  parent: ParentSummary
  students: StudentSummary[]
  recentActivity: ParentActivityEntry[]
}

type ParentPortalContextValue = {
  data: ParentHomePayload
  refresh: () => Promise<void>
}

const TAB_BASE =
  'rounded-full px-4 py-2 text-sm font-semibold transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cocoa'
const TAB_ACTIVE = 'bg-cocoa text-white shadow'
const TAB_INACTIVE = 'bg-white text-cocoa/70 hover:text-cocoa hover:bg-shell'

export default function ParentHome() {
  const [data, setData] = useState<ParentHomePayload | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const mounted = useRef(true)

  useEffect(() => {
    return () => {
      mounted.current = false
    }
  }, [])

  const loadPortal = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const CORE_API = import.meta.env.VITE_CORE_API_URL || ''
      const payload = await jsonFetch<ParentHomePayload>(`${CORE_API}/api/v1/parent/home`)
      if (!mounted.current) return
      setData(payload)
    } catch (err) {
      if (!mounted.current) return
      console.warn('[ParentHome] failed to load portal payload', err)
      setError(err instanceof Error ? err.message : 'Unable to load portal data right now.')
      setData(null)
    } finally {
      if (!mounted.current) return
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadPortal()
  }, [loadPortal])

  if (loading) {
    return <div className="PortalShell">Loading your students…</div>
  }

  if (error) {
    return (
      <div className="PortalShell">
        <section className="Card">
          <h2 className="Title mb-2 text-lg font-semibold">Parent portal unavailable</h2>
          <p className="text-sm text-cocoa/70">{error}</p>
        </section>
      </div>
    )
  }

  if (!data) {
    return (
      <div className="PortalShell">
        <section className="Card">
          <h2 className="Title mb-2 text-lg font-semibold">Parent portal unavailable</h2>
          <p className="text-sm text-cocoa/70">We couldn’t load your students.</p>
        </section>
      </div>
    )
  }

  return (
    <div className="PortalShell">
      <header className="PortalHeader">
        <h1>Welcome, {data.parent?.name || 'Parent'}</h1>
        <p>Stay in the loop with your student’s classroom.</p>
      </header>

      <nav className="PortalTabs mb-6 flex flex-wrap gap-2">
        <NavLink to="/portal/parent" end className={({ isActive }) => `${TAB_BASE} ${isActive ? TAB_ACTIVE : TAB_INACTIVE}`}>
          Home
        </NavLink>
        <NavLink
          to="/portal/parent/messages"
          className={({ isActive }) => `${TAB_BASE} ${isActive ? TAB_ACTIVE : TAB_INACTIVE}`}
        >
          Messages
        </NavLink>
        <NavLink
          to="/portal/parent/support"
          className={({ isActive }) => `${TAB_BASE} ${isActive ? TAB_ACTIVE : TAB_INACTIVE}`}
        >
          Support
        </NavLink>
        <NavLink
          to="/portal/parent/marketplace"
          className={({ isActive }) => `${TAB_BASE} ${isActive ? TAB_ACTIVE : TAB_INACTIVE}`}
        >
          Marketplace
        </NavLink>
      </nav>

      <Outlet context={{ data, refresh: loadPortal }} />
    </div>
  )
}

export function useParentPortalData() {
  return useOutletContext<ParentPortalContextValue>()
}

function formatDate(value?: string | null) {
  if (!value) return ''
  const dt = new Date(value)
  if (Number.isNaN(dt.getTime())) return ''
  return dt.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

export function ParentHomeOverview() {
  const { data } = useParentPortalData()
  const students = Array.isArray(data.students) ? data.students : []
  const recentActivity = Array.isArray(data.recentActivity) ? data.recentActivity : []

  return (
    <div className="space-y-6">
      <CurrentToolsCard role="guardian" manageHref="/portal/parent/marketplace" />

      <section className="Card">
        <h2 className="Title">Your Students</h2>
        <p className="text-sm text-cocoa/70">Linked to your guardian account.</p>

        {students.length === 0 ? (
          <p className="mt-4 text-sm text-cocoa/70">
            No students linked yet. Ask your teacher to add you as a guardian.
          </p>
        ) : (
          <ul className="mt-4 space-y-3">
            {students.map((student) => {
              const s = student as any
              const name = s.displayName || s.display_name || s.fullName || s.full_name || s.id?.slice(0, 8)
              const classroomName = s.classroom?.name || s.classroom?.public_name || ''
              const teacherName = s.classroom?.teacherName || s.teacher?.full_name || ''
              return (
                <li key={student.id} className="CardRow">
                  <div>
                    <div className="Title text-base font-semibold">{name}</div>
                    <div className="Meta text-sm text-cocoa/70">
                      Grade {student.grade || '—'}
                      {classroomName ? ` • ${classroomName}` : ''}
                      {teacherName ? ` (${teacherName})` : ''}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Link to={`/portal/parent/students/${student.id}/profile`} className="Button small outline">
                      Edit Profile
                    </Link>
                    <Link to={`/portal/parent/students/${student.id}`} className="Button small">
                      View updates
                    </Link>
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </section>

      <section className="Card">
        <h2 className="Title">Recent activity</h2>
        <p className="text-sm text-cocoa/70">Teacher notes and classroom highlights will appear here.</p>

        {recentActivity.length === 0 ? (
          <div className="mt-6 rounded-2xl border border-dashed border-sand bg-white px-4 py-6 text-center text-sm text-cocoa/70">
            No activity yet. Teachers can share highlights once they start sending updates.
          </div>
        ) : (
          <ul className="mt-4 space-y-3">
            {recentActivity.map((entry) => (
              <li key={entry.id} className="CardRow items-start gap-4">
                <div className="flex-1 space-y-2">
                  <div className="flex flex-wrap items-center gap-3">
                    <p className="Title text-base font-semibold text-cocoa">
                      {entry.subject || `Update from ${entry.teacherName || 'the classroom'}`}
                    </p>
                    {entry.createdAt ? (
                      <span className="text-xs text-cocoa/60">{formatDate(entry.createdAt)}</span>
                    ) : null}
                  </div>
                  {entry.bodyPreview ? (
                    <p className="whitespace-pre-wrap text-sm text-cocoa/80">{entry.bodyPreview}</p>
                  ) : (
                    <p className="text-sm text-cocoa/70">Your teacher hasn’t added any notes yet.</p>
                  )}
                  <p className="text-xs uppercase tracking-wide text-cocoa/50">
                    {entry.studentName || 'Student'}
                    {entry.classroomName ? ` • ${entry.classroomName}` : ''}
                    {entry.teacherName ? ` (${entry.teacherName})` : ''}
                  </p>
                </div>
                {entry.studentId ? (
                  <Link to={`/portal/parent/students/${entry.studentId}`} className="Button small">
                    Open
                  </Link>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  )
}
